import React, { useState } from 'react';

export interface GammaWizardOption {
  label: string;
  value: string;
  recommended?: boolean;
}

// Mirrors GammaWizardQuestionId in backend/src/types/gamma.ts.
export type GammaWizardQuestionId =
  | 'audience'
  | 'numCards'
  | 'tone'
  | 'format'
  | 'imageSource'
  | 'textAmount';

export interface GammaWizardQuestion {
  id: GammaWizardQuestionId;
  question: string;
  options: GammaWizardOption[];
}

export interface GammaWizardData {
  topic: string;
  questions: GammaWizardQuestion[];
}

interface Props {
  data: GammaWizardData;
  /** Called with { questionId: selectedValue }. Triggers a synthetic chat message in ChatWindow. */
  onSubmit: (answers: Record<string, string>, summary: string) => void;
  /** Called when the user wants to skip the wizard and let Claude proceed with what it knows. */
  onSkip: () => void;
  /** True once the user has submitted; the wizard becomes read-only. */
  submitted?: boolean;
  /** Snapshot of submitted answers, used to render the read-only state. */
  submittedAnswers?: Record<string, string>;
}

const GammaBriefWizard: React.FC<Props> = ({ data, onSubmit, onSkip, submitted, submittedAnswers }) => {
  const [answers, setAnswers] = useState<Record<string, string>>(submittedAnswers || {});

  const allAnswered = data.questions.every((q) => !!answers[q.id]);

  const handlePick = (questionId: string, value: string) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = () => {
    if (!allAnswered || submitted) return;
    const summaryLines = data.questions.map((q) => {
      const picked = answers[q.id];
      return `- ${q.id}: ${picked}`;
    });
    const summary = `Wizard answers:\n${summaryLines.join('\n')}`;
    onSubmit(answers, summary);
  };

  const effectiveAnswers = submitted ? (submittedAnswers || answers) : answers;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <span style={styles.gammaBadge}>GAMMA</span>
        <span style={styles.headerLabel}>{submitted ? 'BRIEF SUBMITTED' : 'BRIEF WIZARD'}</span>
      </div>

      <div style={styles.title}>{data.topic || 'Deck brief'}</div>
      <div style={styles.subtitle}>
        {submitted
          ? 'Generating your deck with these answers…'
          : 'Pick one option per question, then submit. Recommended options are highlighted.'}
      </div>

      {data.questions.map((q) => {
        const picked = effectiveAnswers[q.id];
        return (
          <div key={q.id} style={styles.questionBlock}>
            <div style={styles.question}>{q.question}</div>
            <div style={styles.optionRow}>
              {q.options.map((opt) => {
                const isPicked = picked === opt.value;
                const buttonStyle: React.CSSProperties = {
                  ...styles.optionButton,
                  ...(opt.recommended && !isPicked ? styles.optionButtonRecommended : {}),
                  ...(isPicked ? styles.optionButtonPicked : {}),
                  ...(submitted ? styles.optionButtonDisabled : {}),
                };
                return (
                  <button
                    key={opt.value}
                    type="button"
                    style={buttonStyle}
                    onClick={() => handlePick(q.id, opt.value)}
                    disabled={submitted}
                  >
                    {opt.label}
                    {opt.recommended && !isPicked && !submitted && (
                      <span style={styles.recommendedTag}>recommended</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {!submitted && (
        <div style={styles.actions}>
          <button
            type="button"
            style={{
              ...styles.primaryButton,
              ...(allAnswered ? {} : styles.primaryButtonDisabled),
            }}
            onClick={handleSubmit}
            disabled={!allAnswered}
          >
            {allAnswered ? 'Submit & generate deck' : 'Answer all questions to continue'}
          </button>
          <button type="button" style={styles.secondaryButton} onClick={onSkip}>
            Skip wizard
          </button>
        </div>
      )}
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  card: {
    marginTop: 12,
    padding: '14px 16px',
    background: 'linear-gradient(135deg, rgba(135, 95, 255, 0.06), rgba(255, 90, 180, 0.04))',
    border: '1px solid rgba(135, 95, 255, 0.25)',
    borderRadius: 'var(--radius-md)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  gammaBadge: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    padding: '2px 6px',
    borderRadius: 4,
    background: 'var(--cg-gradient-hero)',
    color: '#fff',
  },
  headerLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.06em',
    color: 'var(--fg-2)',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--fg-1)',
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: 12,
    color: 'var(--fg-2)',
    marginTop: -4,
  },
  questionBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  question: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  optionRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  optionButton: {
    padding: '8px 12px',
    background: '#fff',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 200ms var(--ease-standard)',
    fontFamily: 'var(--font-sans)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  optionButtonRecommended: {
    borderColor: 'rgba(135, 95, 255, 0.45)',
    background: 'rgba(135, 95, 255, 0.04)',
  },
  optionButtonPicked: {
    background: 'var(--cg-gradient-hero)',
    color: '#fff',
    borderColor: 'transparent',
    fontWeight: 600,
  },
  optionButtonDisabled: {
    opacity: 0.7,
    cursor: 'default',
  },
  recommendedTag: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '1px 5px',
    borderRadius: 3,
    background: 'rgba(135, 95, 255, 0.15)',
    color: 'var(--accent)',
  },
  actions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 4,
  },
  primaryButton: {
    padding: '8px 14px',
    background: 'var(--cg-gradient-hero)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 200ms',
    fontFamily: 'var(--font-sans)',
  },
  primaryButtonDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  secondaryButton: {
    padding: '8px 14px',
    background: '#fff',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
};

export default GammaBriefWizard;
